import React, { useState, useRef, useEffect } from 'react';
import {
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input
} from 'reactstrap';

const CommonTable = ({
  columns,
  data,
  totalRecords = 0,
  pageSize = 10,
  onPageChange,
  onSelectionChange
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [colWidths, setColWidths] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [isResizing, setIsResizing] = useState(false);

  const totalPages = Math.ceil(totalRecords / pageSize);

  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const dragColIndex = useRef(null);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      const offset = (page - 1) * pageSize;
      onPageChange?.(pageSize, offset, searchFilters);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchChange = (accessor, value) => {
    const updatedFilters = { ...searchFilters, [accessor]: value };
    setSearchFilters(updatedFilters);
    setCurrentPage(1);
    onPageChange?.(pageSize, 0, updatedFilters, false);
  };

  const onMouseDown = (e, accessor) => {
    setIsResizing(true);
    resizingCol.current = accessor;
    startX.current = e.clientX;
    startWidth.current = colWidths[accessor] || e.target.parentElement.offsetWidth;

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!resizingCol.current) return;
    const deltaX = e.clientX - startX.current;
    const newWidth = Math.max(startWidth.current + deltaX, 50);

    setColWidths(prev => ({
      ...prev,
      [resizingCol.current]: newWidth
    }));
  };

  const onMouseUp = () => {
    setIsResizing(false);
    resizingCol.current = null;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  const toggleRowSelection = (rowIndex) => {
    const exists = selectedRows.find(r => r.__rowIndex === rowIndex);
    let newSelection;
    if (exists) {
      newSelection = selectedRows.filter(r => r.__rowIndex !== rowIndex);
    } else {
      newSelection = [...selectedRows, { ...data[rowIndex], __rowIndex: rowIndex }];
    }
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection.map(({ __rowIndex, ...row }) => row));
  };

  useEffect(() => {
    if (Object.keys(colWidths).length === 0 && columns.length) {
      const initialWidths = { __serial: 60 };
      if (onSelectionChange) initialWidths.__checkbox = 50;
      columns.forEach(col => {
        initialWidths[col.accessor || col.header] = 150; // default numeric width
      });
      setColWidths(initialWidths);
    }
    if (columnOrder.length === 0 && columns.length) {
      const baseCols = [];
      if (onSelectionChange) baseCols.push('__checkbox');
      baseCols.push('__serial');
      const uniqueCols = [...new Set(columns.map(c => c.accessor || c.header))];
      setColumnOrder([...baseCols, ...uniqueCols]);
    }
  }, [columns, onSelectionChange]);

  const computedColumnsMap = {};

  if (onSelectionChange) {
    computedColumnsMap['__checkbox'] = {
      accessor: '__checkbox',
      header: (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Input
            type="checkbox"
            checked={data.length > 0 && data.every((_, i) => selectedRows.find(r => r.__rowIndex === i))}
            onChange={(e) => {
              let newSelection = [];
              if (e.target.checked) {
                newSelection = [
                  ...selectedRows.filter(r => !data.some((_, i) => r.__rowIndex === i)),
                  ...data.map((row, i) => ({ ...row, __rowIndex: i }))
                ];
              } else {
                newSelection = selectedRows.filter(r => !data.some((_, i) => r.__rowIndex === i));
              }
              setSelectedRows(newSelection);
              onSelectionChange?.(newSelection.map(({ __rowIndex, ...rest }) => rest));
            }}
          />
        </div>
      ),
      render: (_, rowIndex) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Input
            type="checkbox"
            checked={!!selectedRows.find(r => r.__rowIndex === rowIndex)}
            onChange={() => toggleRowSelection(rowIndex)}
          />
        </div>
      )
    };
  }

  computedColumnsMap['__serial'] = {
    accessor: '__serial',
    header: 'S.No',
    render: (_, rowIndex) => (currentPage - 1) * pageSize + rowIndex + 1
  };

  columns.forEach(col => {
    computedColumnsMap[col.accessor || col.header] = col;
  });

  const computedColumns = [...new Set(columnOrder)]
    .map(key => computedColumnsMap[key])
    .filter(Boolean);

  const handleDragStart = (index) => {
    dragColIndex.current = index;
  };

  const handleDrop = (index) => {
    const newOrder = [...columnOrder];
    const [removed] = newOrder.splice(dragColIndex.current, 1);
    newOrder.splice(index, 0, removed);
    setColumnOrder(newOrder);
    dragColIndex.current = null;
  };

  const renderPagination = () => {
    const pages = [];
    const blocks = 30;

    const addPage = (page) => {
      pages.push(
        <PaginationItem active={currentPage === page} key={page}>
          <PaginationLink onClick={() => handlePageChange(page)}>
            {page}
          </PaginationLink>
        </PaginationItem>
      );
    };

    const addEllipsis = (key) => {
      pages.push(
        <PaginationItem disabled key={key}>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      );
    };

    const range = (start, end) => {
      const res = [];
      for (let i = start; i <= end; i++) res.push(i);
      return res;
    };

    if (totalPages <= blocks + 3) {
      range(1, totalPages).forEach(addPage);
    } else {
      const currentBlockStart = Math.floor((currentPage - 1) / blocks) * blocks + 1;
      const currentBlockEnd = Math.min(currentBlockStart + blocks - 1, totalPages);

      if (currentBlockStart > 1) {
        addPage(1);
        if (currentBlockStart > 2) addEllipsis('start');
      }

      for (let i = currentBlockStart; i <= currentBlockEnd; i++) {
        addPage(i);
      }

      if (currentBlockEnd < totalPages - 3) {
        addEllipsis('middle');
      }

      for (let i = totalPages - 2; i <= totalPages; i++) {
        addPage(i);
      }
    }

    return (
      <Pagination>
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  return (
    <div>
      <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
        <Table
          striped
          bordered
          hover
          style={{
            tableLayout: 'fixed',
            width: `100%`,
            borderCollapse: 'separate',
            borderSpacing: 0
          }}
        >
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 3 }}>
            <tr>
              {computedColumns.map((col, index) => (
                <th
                  key={col.accessor || col.header}
                  draggable={!isResizing}
                  className={col.class || ""}
                  onDragStart={(e) => {
                    if (isResizing) {
                      e.preventDefault();
                      return;
                    }
                    handleDragStart(index);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(index)}
                  style={{
                    width: colWidths[col.accessor || col.header],
                    position: 'relative',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    padding: '8px',
                    boxSizing: 'border-box'
                  }}
                >
                  {col.header}
                  <div
                    className="resize-handle"
                    onMouseDown={(e) => onMouseDown(e, col.accessor || col.header)}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      width: '5px',
                      cursor: 'col-resize',
                      zIndex: 10,
                      userSelect: 'none'
                    }}
                  />
                </th>
              ))}
            </tr>
            <tr style={{ position: 'sticky', top: '38px', backgroundColor: '#f8f9fa', zIndex: 2 }}>
              {computedColumns.map((col) => (
                <th
                  key={col.accessor || col.header}
                  style={{ width: colWidths[col.accessor || col.header], padding: '0', boxSizing: 'border-box' }}
                >
                  {col.search ? (
                    <Input
                      type="text"
                      value={searchFilters[col.accessor || col.header] || ''}
                      onChange={(e) => handleSearchChange(col.accessor || col.header, e.target.value)}
                      placeholder={`Search ${col.header}`}
                      style={{ margin: 0, border: 'none', borderBottom: '1px solid #ccc', borderRadius: 0 }}
                    />
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {computedColumns.map((col) => (
                    <td
                      key={col.accessor || col.header}
                      className={col.class || ""}
                      style={{
                        width: colWidths[col.accessor || col.header],
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '8px',
                        boxSizing: 'border-box'
                      }}
                    >
                      {col.render ? col.render(row, rowIndex) : row[col.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={computedColumns.length} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-center mt-3">
        {renderPagination()}
      </div>
    </div>
  );
};

export default CommonTable;
